import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives/responsive";
import { N200 } from "@atlaskit/theme/colors";

import {
    fontSize,
    getFirstName,
    getProductData,
    ProductData,
    ProductKey,
    getSwitchAccountUrl
} from "@/components/signup/utils";
import { useProductD0to6Site, useUserContext } from "@/components/user-context";
import { wacToken } from "@/libraries/design/foundations";

import { useSignupFormContext } from "../../../signup-form/hooks";
import { DefaultSiteNameLogo } from "../variants/default-site-name";

export const ReturnToExistingProduct = ({
    exitView,
    productKeyForExpand
}: {
    exitView: () => void;
    productKeyForExpand: ProductKey;
}) => {
    const d0to6JiraSite = useProductD0to6Site({
        productKey: productKeyForExpand
    });
    const { productKey } = useSignupFormContext();
    const {
        user: { email, name }
    } = useUserContext();
    const { productName } = getProductData(
        productKey as keyof typeof ProductData
    );
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    const goToProductButtonText =
        productKeyForExpand === ProductKey.CONFLUENCE
            ? intl.formatMessage({
                  id: "bxpSignup.siteExpansion.goToWorkspace",
                  defaultMessage: "Go to workspace"
              })
            : productKeyForExpand === ProductKey.JIRA_SOFTWARE
            ? intl.formatMessage({
                  id: "JiraSoftwarePageTemplate.loggedInHero.goToJira",
                  defaultMessage: "Go to Jira"
              })
            : intl.formatMessage(
                  {
                      id: "BxpSignup.siteExpansion.goToProduct",
                      defaultMessage: "Go to {productName}"
                  },
                  {
                      productName
                  }
              );

    const CreateNewSiteLink = () =>
        productKeyForExpand === ProductKey.CONFLUENCE ? (
            <div css={createANewWorkspaceLink}>
                <FormattedMessage
                    id="BxpSignup.SiteExpansionForm.createNewWorkspaceLink"
                    defaultMessage="Or<link>create a new workspace</link>"
                    values={{
                        link: (str: ReactNode) => (
                            <Button appearance="link" onClick={exitView}>
                                {str}
                            </Button>
                        )
                    }}
                />
            </div>
        ) : (
            <Button appearance="link" onClick={exitView}>
                <FormattedMessage
                    id="WacSignupForm.upgradeNewSiteSignupLinkText"
                    defaultMessage="Or start a new site"
                />
            </Button>
        );

    return !d0to6JiraSite ? null : (
        <>
            <DefaultSiteNameLogo />
            <h1>
                <FormattedMessage
                    id="BxpSignup.SiteExpansionForm.headerNoUnderline"
                    defaultMessage="Welcome back, {name}"
                    values={{
                        name: getFirstName(name)
                    }}
                />
            </h1>
            <h2 css={extendWidthCss}>
                <FormattedMessage
                    id="BxpSignup.SiteExpansionForm.subheader.1"
                    defaultMessage="You’re logged in as {email} <switchAccountLink>Switch account</switchAccountLink>"
                    values={{
                        email: <strong css={emailCss}>{email}</strong>,
                        switchAccountLink: (children) => (
                            <a
                                href={getSwitchAccountUrl()}
                                css={switchAccountLinkCss}
                            >
                                {children}
                            </a>
                        )
                    }}
                />
            </h2>
            <div css={boxContainerCss} data-testid="return-to-existing-product">
                <a
                    css={siteBoxCss}
                    href={getProductUrl({ url: d0to6JiraSite.url, productKey })}
                    onClick={(e) => {
                        e.preventDefault(); // wait for event to fire
                        createAnalyticsEvent({
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "button",
                            actionSubjectId:
                                "contactExpandClickReturnToProductButton",
                            attributes: {
                                productName
                            }
                        }).fire();
                        window.location.href = getProductUrl({
                            url: d0to6JiraSite.url,
                            productKey
                        });
                    }}
                >
                    <div css={siteCss}>
                        <div css={semiBoldCss}>{productName}</div>
                        <div css={siteNameCss}>{d0to6JiraSite.displayName}</div>
                    </div>
                    <Button appearance="primary">
                        {goToProductButtonText}
                    </Button>
                </a>
            </div>
            <CreateNewSiteLink />
        </>
    );
};

const getProductUrl = ({
    url,
    productKey
}: {
    url: string;
    productKey: string;
}) => {
    switch (productKey) {
        case ProductKey.JIRA_SOFTWARE:
            return `${url}/jira`;
        case ProductKey.CONFLUENCE:
            return `${url}/wiki`;
        default:
            return url;
    }
};
const createANewWorkspaceLink = css({
    alignSelf: "flex-start",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        marginBlockStart: "-3px",
        marginInlineStart: "-8px"
    }
});
const boxContainerCss = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    margin: "1rem 0 16px",
    width: "100%",
    [media.above.md]: {
        margin: "2rem 0"
    },
    border: `0.063rem solid ${wacToken("color.neutral.light400")}`,
    padding: "16px",
    borderRadius: "0.313rem"
});

const semiBoldCss = css({
    fontWeight: "600"
});

const siteBoxCss = css({
    alignItems: "center",
    color: "inherit !important",
    display: "flex",
    gap: "0.5rem",
    justifyContent: "space-between",
    padding: "1rem 0",
    width: "100%",
    [":hover"]: {
        textDecoration: "none !important"
    },
    [":not(:first-of-type)"]: {
        borderBlockStart: `1px solid ${wacToken("color.neutral.light300")}`
    }
});

const siteCss = css({
    fontSize: fontSize.md,
    overflow: "hidden"
});

const siteNameCss = css({
    color: N200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBlockStart: "2px"
});

const emailCss = css({
    fontWeight: 600,
    hyphens: "auto",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    wordWrap: "break-word"
});

const switchAccountLinkCss = css({
    whiteSpace: "nowrap"
});

const extendWidthCss = css({
    [media.above.sm]: {
        marginInlineStart: "-100px !important",
        marginInlineEnd: "-100px !important"
    }
});
